import { useState, useEffect, useCallback, useRef } from "react";
import {
  FiCloud,
  FiFolder,
  FiPlus,
  FiArrowLeft,
  FiImage,
  FiSettings,
} from "react-icons/fi";
import { useTranslation } from "../context/TranslationContext";
import ConfirmModal from "../components/ConfirmModal";
import PromptModal from "../components/PromptModal";
import CloudFileIcon from "../components/CloudFileIcon";
import CustomVideoPlayer from "../components/CustomVideoPlayer";
import Button from "../components/ds/Button";
import EmptyState from "../components/ds/EmptyState";
import Icon from "../components/ds/Icon";
import IconButton, { STATE_LAYER } from "../components/ds/IconButton";
import Modal from "../components/ds/Modal";
import TextField from "../components/ds/TextField";
import MintSpaceAppWindow from "../components/MintSpaceAppWindow";
import {
  browseMintSpaceCloud,
  createCloudFolder,
  updateCloudFolder,
  deleteCloudFolder,
  uploadCloudFile,
  updateCloudFile,
  deleteCloudFile,
  getCloudFileContent,
  saveCloudFileContent,
  fetchCloudFileBlob,
  fetchCloudFilePreviewBlob,
  fetchCloudWallpaper,
  saveCloudWallpaper,
} from "../api/mintSpaceCloud";
import {
  classifyCloudFile,
  canPreviewCloudFile,
  isPreviewableCloudFile,
  isEditableTextFile,
  isRawCloudImage,
} from "../utils/cloudFileTypes";
import { readWallpaperImageFile } from "../utils/cloudWallpaper";

const formatBytes = (bytes) => {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const FILLED = "!bg-app-cloud-base !text-app-cloud-on";

/** Figma E34: a card's actions, which wait for a hover where there is one. */
const CARD_ACTIONS =
  "flex h-8 justify-end gap-space-4 transition-opacity [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 [@media(hover:hover)]:focus-within:opacity-100";

/** Figma E35: the name becomes a field. Enter or leaving it saves, Esc cancels. */
const RenameField = ({ name, value, onChange, onSave, onCancel }) => (
  <TextField
    autoFocus
    aria-label={name}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    onKeyDown={(e) => {
      if (e.key === "Enter") onSave();
      if (e.key === "Escape") onCancel();
    }}
    onBlur={() => (value.trim() === name ? onCancel() : onSave())}
  />
);

const MintSpaceCloudPage = ({
  isDark,
  setToast,
  mintSpace,
  isOwner = false,
  isAdmin = false,
  appInstanceId,
  onCreateProduct,
  onOpenProperties,
}) => {
  const { t } = useTranslation();
  const canManage = isOwner;
  const fileInputRef = useRef(null);
  const wallpaperInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [dragItem, setDragItem] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [renaming, setRenaming] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewText, setPreviewText] = useState("");
  const [previewMediaUrl, setPreviewMediaUrl] = useState(null);
  const previewMediaUrlRef = useRef(null);
  const [previewEditing, setPreviewEditing] = useState(false);
  const [previewSaving, setPreviewSaving] = useState(false);
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [deleteFolderTarget, setDeleteFolderTarget] = useState(null);
  const [deleteFileTarget, setDeleteFileTarget] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [wallpaper, setWallpaper] = useState({ image: null, opacity: 0.28 });
  const [showWallpaperPanel, setShowWallpaperPanel] = useState(false);
  const [useNativeVideoControls, setUseNativeVideoControls] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 1024 : false
  );

  const mintSpaceId = mintSpace?._id;
  const baseText = "text-label-primary";
  const mutedText = "text-label-secondary";

  useEffect(() => {
    if (!mintSpaceId) return;
    fetchCloudWallpaper(mintSpaceId, appInstanceId)
      .then((res) => {
        if (res.success) {
          setWallpaper({
            image: res.data?.image || null,
            opacity: typeof res.data?.opacity === "number" ? res.data.opacity : 0.28,
          });
        }
      })
      .catch(() => {});
  }, [mintSpaceId]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const update = () => setUseNativeVideoControls(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const persistWallpaper = async (next) => {
    setWallpaper(next);
    if (!mintSpaceId || !canManage) return;
    try {
      const res = await saveCloudWallpaper(mintSpaceId, next, appInstanceId);
      if (res.success) {
        setWallpaper({
          image: res.data?.image || null,
          opacity: typeof res.data?.opacity === "number" ? res.data.opacity : next.opacity,
        });
      }
    } catch (err) {
      if (setToast) setToast(err.response?.data?.message || t("cloud.wallpaperFailed"));
    }
  };

  const handleWallpaperUpload = async (fileList) => {
    const file = fileList?.[0];
    if (!file || !file.type.startsWith("image/")) {
      if (setToast) setToast(t("cloud.wallpaperImageOnly"));
      return;
    }
    try {
      const image = await readWallpaperImageFile(file);
      persistWallpaper({ ...wallpaper, image });
      if (setToast) setToast(t("cloud.wallpaperSet"));
    } catch {
      if (setToast) setToast(t("cloud.wallpaperFailed"));
    }
  };

  const loadCloud = useCallback(async () => {
    if (!mintSpaceId) return;
    try {
      setLoading(true);
      const res = await browseMintSpaceCloud(mintSpaceId, currentFolderId, appInstanceId);
      if (res.success) {
        const data = res.data || {};
        setFolders(data.folders || []);
        setFiles(data.files || []);
        setBreadcrumb(data.breadcrumb || []);
      }
    } catch (err) {
      if (setToast) setToast(err.message || t("cloud.failedToLoad"));
    } finally {
      setLoading(false);
    }
  }, [mintSpaceId, currentFolderId, appInstanceId, setToast, t]);

  useEffect(() => {
    loadCloud();
  }, [loadCloud]);

  useEffect(
    () => () => {
      if (previewMediaUrlRef.current) {
        URL.revokeObjectURL(previewMediaUrlRef.current);
      }
    },
    []
  );

  const openFolder = (folderId) => setCurrentFolderId(folderId);

  const goBack = () => {
    if (breadcrumb.length === 0) return;
    const parent = breadcrumb.length > 1 ? breadcrumb[breadcrumb.length - 2]._id : null;
    setCurrentFolderId(parent);
  };

  const goToCrumb = (folderId) => setCurrentFolderId(folderId);

  /** Parent of the folder we're viewing — null at desktop root. */
  const parentFolderId =
    breadcrumb.length <= 1 ? null : breadcrumb[breadcrumb.length - 2]._id;

  const isInternalDrag = (e) =>
    e.dataTransfer.types.includes("text/plain") && !e.dataTransfer.files?.length;

  const handleCreateFolder = async (name) => {
    try {
      setModalLoading(true);
      const res = await createCloudFolder(mintSpaceId, {
        name,
        parentFolder: currentFolderId,
        instanceId: appInstanceId,
      });
      if (res.success) {
        setShowNewFolderModal(false);
        await loadCloud();
        if (setToast) setToast(t("cloud.folderCreated"));
      }
    } catch (err) {
      if (setToast) setToast(err.message || t("cloud.failedToSave"));
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteFolder = async () => {
    if (!deleteFolderTarget) return;
    try {
      setModalLoading(true);
      const res = await deleteCloudFolder(mintSpaceId, deleteFolderTarget, appInstanceId);
      if (res.success) {
        setDeleteFolderTarget(null);
        await loadCloud();
        if (setToast) setToast(t("cloud.folderDeleted"));
      }
    } catch (err) {
      if (setToast) setToast(err.message || t("cloud.failedToDelete"));
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteFile = async () => {
    if (!deleteFileTarget) return;
    try {
      setModalLoading(true);
      const res = await deleteCloudFile(mintSpaceId, deleteFileTarget, appInstanceId);
      if (res.success) {
        setDeleteFileTarget(null);
        await loadCloud();
        if (setToast) setToast(t("cloud.fileDeleted"));
      }
    } catch (err) {
      if (setToast) setToast(err.message || t("cloud.failedToDelete"));
    } finally {
      setModalLoading(false);
    }
  };

  const handleUpload = async (fileList) => {
    if (!canManage || !fileList?.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(fileList)) {
        await uploadCloudFile(mintSpaceId, file, currentFolderId, appInstanceId);
      }
      await loadCloud();
      if (setToast) setToast(t("cloud.uploaded"));
    } catch (err) {
      if (setToast) setToast(err.message || t("cloud.failedToUpload"));
    } finally {
      setUploading(false);
    }
  };

  const startRename = (type, item) => {
    setRenaming({ type, id: item._id });
    setRenameValue(item.name);
  };

  const cancelRename = () => {
    setRenaming(null);
    setRenameValue("");
  };

  const submitRename = async () => {
    if (!renaming || !renameValue.trim()) return;
    try {
      const payload = { name: renameValue.trim(), instanceId: appInstanceId };
      const res =
        renaming.type === "folder"
          ? await updateCloudFolder(mintSpaceId, renaming.id, payload)
          : await updateCloudFile(mintSpaceId, renaming.id, payload);
      if (res.success) {
        cancelRename();
        await loadCloud();
        if (setToast) setToast(t("cloud.renamed"));
      }
    } catch (err) {
      if (setToast) setToast(err.message || t("cloud.failedToSave"));
    }
  };

  const moveItem = async (item, targetFolderId) => {
    if (!canManage) return;
    try {
      const payload =
        item.type === "folder"
          ? { parentFolder: targetFolderId, instanceId: appInstanceId }
          : { folderId: targetFolderId, instanceId: appInstanceId };
      const res =
        item.type === "folder"
          ? await updateCloudFolder(mintSpaceId, item.id, payload)
          : await updateCloudFile(mintSpaceId, item.id, payload);
      if (res.success) {
        await loadCloud();
        if (setToast) setToast(t("cloud.moved"));
      }
    } catch (err) {
      if (setToast) setToast(err.message || t("cloud.failedToMove"));
    }
  };

  const onDragStart = (e, type, id) => {
    if (!canManage) return;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", `${type}:${id}`);
    setDragItem({ type, id });
  };

  const onDragEnd = () => {
    setDragItem(null);
    setDropTarget(null);
  };

  const onDragOver = (e, targetId) => {
    if (!isInternalDrag(e) && !e.dataTransfer.types.includes("Files")) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = isInternalDrag(e) ? "move" : "copy";
    setDropTarget(targetId);
  };

  const onDragLeave = (e, targetId) => {
    if (dropTarget === targetId) setDropTarget(null);
  };

  const onDropOnFolder = async (e, folderId) => {
    e.preventDefault();
    e.stopPropagation();
    setDropTarget(null);
    if (e.dataTransfer.files?.length) {
      await handleUpload(e.dataTransfer.files);
      return;
    }
    if (!dragItem || (dragItem.type === "folder" && dragItem.id === folderId)) return;
    await moveItem(dragItem, folderId);
    setDragItem(null);
  };

  const onDropToFolder = async (e, targetFolderId) => {
    e.preventDefault();
    e.stopPropagation();
    setDropTarget(null);
    if (e.dataTransfer.files?.length) {
      await handleUpload(e.dataTransfer.files);
      return;
    }
    if (!dragItem) return;
    await moveItem(dragItem, targetFolderId);
    setDragItem(null);
  };

  /** Drop on empty desktop — moves items up to the parent folder (or root). */
  const onDropOnDesktop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDropTarget(null);
    if (e.dataTransfer.files?.length) {
      await handleUpload(e.dataTransfer.files);
      return;
    }
    if (!dragItem) return;
    const targetId = breadcrumb.length > 0 ? parentFolderId : null;
    await moveItem(dragItem, targetId);
    setDragItem(null);
  };

  const revokePreviewMediaUrl = () => {
    if (previewMediaUrlRef.current) {
      URL.revokeObjectURL(previewMediaUrlRef.current);
      previewMediaUrlRef.current = null;
    }
    setPreviewMediaUrl(null);
  };

  const closePreview = () => {
    revokePreviewMediaUrl();
    setPreview(null);
    setPreviewText("");
    setPreviewEditing(false);
    setPreviewLoading(false);
  };

  const loadPreviewMedia = async (file, kind) => {
    setPreviewLoading(true);
    try {
      const useRawPreview = kind === "image" && isRawCloudImage(file);
      const blob = useRawPreview
        ? await fetchCloudFilePreviewBlob(mintSpaceId, file._id, appInstanceId)
        : await fetchCloudFileBlob(mintSpaceId, file._id, appInstanceId);
      const mime = useRawPreview
        ? "image/jpeg"
        : file.mimeType ||
          blob.type ||
          (kind === "image" ? "image/jpeg" : kind === "mp4" ? "video/mp4" : "audio/mpeg");
      const objectUrl = URL.createObjectURL(new Blob([blob], { type: mime }));
      revokePreviewMediaUrl();
      previewMediaUrlRef.current = objectUrl;
      setPreviewMediaUrl(objectUrl);
    } catch (err) {
      let message = err.message || t("cloud.previewFailed");
      const data = err.response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          if (parsed?.message) message = parsed.message;
        } catch {
          // keep default message
        }
      } else if (err.response?.data?.message) {
        message = err.response.data.message;
      }
      if (setToast) setToast(message);
      closePreview();
    } finally {
      setPreviewLoading(false);
    }
  };

  const openPreview = async (file) => {
    if (!canPreviewCloudFile(file)) {
      if (setToast) setToast(t("cloud.previewTooLarge"));
      return;
    }
    const kind = classifyCloudFile(file);
    revokePreviewMediaUrl();
    setPreview({ file, kind });
    setPreviewText("");
    setPreviewEditing(false);

    if (kind === "text") {
      setPreviewLoading(true);
      try {
        const res = await getCloudFileContent(mintSpaceId, file._id, appInstanceId);
        if (res.success) {
          setPreviewText(res.data?.content ?? "");
        } else {
          throw new Error(res.message);
        }
      } catch (err) {
        if (setToast) setToast(err.message || t("cloud.previewFailed"));
        closePreview();
      } finally {
        setPreviewLoading(false);
      }
      return;
    }

    if (kind === "image" || kind === "mp3" || kind === "mp4") {
      await loadPreviewMedia(file, kind);
    }
  };

  const savePreviewText = async () => {
    if (!preview?.file) return;
    setPreviewSaving(true);
    try {
      const res = await saveCloudFileContent(mintSpaceId, preview.file._id, previewText, appInstanceId);
      if (res.success) {
        setPreview((prev) => (prev ? { ...prev, file: res.data } : prev));
        setPreviewEditing(false);
        await loadCloud();
        if (setToast) setToast(t("cloud.contentSaved"));
      }
    } catch (err) {
      if (setToast) setToast(err.message || t("cloud.failedToSave"));
    } finally {
      setPreviewSaving(false);
    }
  };

  const desktopBg = "";

  const fileCard = (isTarget) =>
    `group flex min-w-0 flex-col gap-space-8 rounded-radius-lg border bg-bg-raised p-space-12 transition ${
      isTarget ? "border-accent-default" : "border-transparent hover:border-accent-default"
    }`;

  const propertyMenuItems = [
    isAdmin &&
      onOpenProperties && {
        id: "properties",
        label: t("mintSpace.appProperties") || "Properties",
        icon: FiSettings,
        onClick: onOpenProperties,
      },
    canManage && {
      id: "background",
      label: t("cloud.background"),
      icon: FiImage,
      onClick: () => setShowWallpaperPanel((v) => !v),
    },
    isAdmin &&
      onCreateProduct && {
        id: "create-product",
        label: t("mintSpace.createProduct") || "Create product",
        icon: FiPlus,
        onClick: onCreateProduct,
      },
  ].filter(Boolean);

  return (
    <MintSpaceAppWindow
      isDark={isDark}
      app="cloud"
      icon={FiCloud}
      iconGradient="from-sky-400 to-blue-600"
      title={t("cloud.title")}
      subtitle={t("cloud.subtitle")}
      menuItems={propertyMenuItems}
      actions={
        canManage ? (
          <>
            <Button
              size="m"
              icon={<Icon name="plus" size={20} />}
              className={uploading ? "" : FILLED}
              loading={uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? t("cloud.uploading") : t("cloud.upload")}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                handleUpload(e.target.files);
                e.target.value = "";
              }}
            />
            <input
              ref={wallpaperInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                handleWallpaperUpload(e.target.files);
                e.target.value = "";
              }}
            />
          </>
        ) : null
      }
      toolbar={
        <>
          {canManage && showWallpaperPanel && (
            <div className="mb-space-12 flex shrink-0 flex-wrap items-center gap-space-12 rounded-radius-lg bg-bg-raised p-space-16">
              <p className="text-footnote-semibold text-label-primary">{t("cloud.background")}</p>
              <Button size="s" className={FILLED} onClick={() => wallpaperInputRef.current?.click()}>
                {t("cloud.uploadBackground")}
              </Button>
              <Button
                size="s"
                variant="gray"
                onClick={async () => {
                  await persistWallpaper({ image: null, opacity: wallpaper.opacity });
                  if (setToast) setToast(t("cloud.wallpaperCleared"));
                }}
              >
                {t("cloud.clearBackground")}
              </Button>
              <label className="flex items-center gap-space-8 text-footnote text-label-secondary">
                <span>{t("cloud.backgroundOpacity")}</span>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="1"
                  value={Math.round(wallpaper.opacity * 100)}
                  onChange={(e) => persistWallpaper({ ...wallpaper, opacity: Number(e.target.value) / 100 })}
                  className="w-28 accent-app-cloud-base"
                />
                <span className="min-w-[2.5rem] tabular-nums text-label-primary">{Math.round(wallpaper.opacity * 100)}%</span>
              </label>
            </div>
          )}
          {canManage && (
            <Button
              size="s"
              variant="gray"
              className="mb-space-16 self-start"
              icon={<Icon name="plus" size={16} />}
              onClick={() => setShowNewFolderModal(true)}
            >
              {t("cloud.newFolder")}
            </Button>
          )}
          <div
            className={`flex shrink-0 items-center gap-space-8 rounded-14 text-footnote-semibold text-label-secondary ${
              dropTarget === "breadcrumb-root" ? "ring-2 ring-accent-default" : ""
            }`}
          >
        {breadcrumb.length > 0 && (
          <button
            type="button"
            onClick={goBack}
            className="rounded-full p-space-4 hover:bg-fill-quaternary"
            aria-label={t("cloud.back")}
          >
            <FiArrowLeft className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          onClick={() => setCurrentFolderId(null)}
          onDragOver={(e) => onDragOver(e, "breadcrumb-root")}
          onDragLeave={(e) => onDragLeave(e, "breadcrumb-root")}
          onDrop={(e) => onDropToFolder(e, null)}
          className={`rounded px-1.5 py-0.5 font-medium transition ${
            !currentFolderId ? "text-accent-text" : "hover:underline"
          } ${dropTarget === "breadcrumb-root" ? "bg-accent-tinted ring-1 ring-accent-default" : ""}`}
        >
          <span className="inline-flex items-center gap-space-8">
            <Icon name="home" size={16} />
            {t("cloud.desktop")}
          </span>
        </button>
        {breadcrumb.map((crumb, index) => {
          const crumbTargetId = `breadcrumb-${crumb._id}`;
          const isLast = index === breadcrumb.length - 1;
          return (
            <span key={crumb._id} className="flex items-center gap-2">
              <span>/</span>
              <button
                type="button"
                onClick={() => goToCrumb(crumb._id)}
                onDragOver={(e) => !isLast && onDragOver(e, crumbTargetId)}
                onDragLeave={(e) => onDragLeave(e, crumbTargetId)}
                onDrop={(e) => !isLast && onDropToFolder(e, crumb._id)}
                className={`max-w-[120px] truncate rounded px-1.5 py-0.5 transition ${
                  isLast ? "font-semibold text-accent-text" : "hover:underline"
                } ${dropTarget === crumbTargetId ? "bg-accent-tinted ring-1 ring-accent-default" : ""}`}
              >
                {crumb.name}
              </button>
            </span>
          );
        })}
        {dragItem && canManage && breadcrumb.length > 0 && (
          <span className={`ml-auto hidden text-caption-1 sm:inline ${mutedText}`}>
            {t("cloud.dropToMoveUp")}
          </span>
        )}
          </div>
        </>
      }
    >
      {/* Desktop area */}
      <div
        className={`relative h-full min-h-full overflow-auto rounded-radius-lg ${wallpaper.image ? "p-space-16" : ""} ${
          dropTarget === "desktop" ? "ring-2 ring-inset ring-accent-default" : ""
        }`}
        onDragOver={(e) => onDragOver(e, "desktop")}
        onDragLeave={(e) => onDragLeave(e, "desktop")}
        onDrop={onDropOnDesktop}
      >
        {wallpaper.image && (
          <>
            <div
              className="pointer-events-none absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${wallpaper.image})`, opacity: wallpaper.opacity }}
            />
            <div
              className={`pointer-events-none absolute inset-0 ${
                "bg-bg-base opacity-50"
              }`}
              style={{ opacity: wallpaper.opacity }}
            />
          </>
        )}
        <div className="relative z-10 min-h-full">
        {loading ? (
          <div className={`flex h-full items-center justify-center text-subheadline ${mutedText}`}>
            {t("cloud.loading")}
          </div>
        ) : folders.length === 0 && files.length === 0 ? (
          <div
            className={`flex h-full flex-col items-center justify-center rounded-radius-lg text-center ${
              dropTarget === "desktop" ? "bg-accent-tinted" : ""
            }`}
            onDragOver={(e) => onDragOver(e, "desktop")}
            onDrop={onDropOnDesktop}
          >
            <EmptyState icon="upload" title={t("cloud.emptyTitle")} message={t("cloud.emptyHint")} />
            {dragItem && canManage && breadcrumb.length > 0 && (
              <p className={`mt-3 text-footnote ${mutedText}`}>{t("cloud.dropToMoveUp")}</p>
            )}
          </div>
        ) : (
          <div
            className="grid min-h-full grid-cols-[repeat(auto-fill,minmax(160px,200px))] content-start gap-space-12"
            onDragOver={(e) => onDragOver(e, "desktop")}
            onDrop={onDropOnDesktop}
          >
            {folders.map((folder) => {
              const isRenaming = renaming?.type === "folder" && renaming.id === folder._id;
              return (
                <div
                  key={folder._id}
                  draggable={canManage && !isRenaming}
                  onDragStart={(e) => onDragStart(e, "folder", folder._id)}
                  onDragEnd={onDragEnd}
                  onDragOver={(e) => {
                    e.stopPropagation();
                    onDragOver(e, folder._id);
                  }}
                  onDrop={(e) => onDropOnFolder(e, folder._id)}
                  onDoubleClick={() => openFolder(folder._id)}
                  className={fileCard(dropTarget === folder._id)}
                >
                  {!isRenaming && canManage && (
                    <div className={CARD_ACTIONS}>
                      <IconButton variant="plain" label={t("common.edit")} onClick={() => startRename("folder", folder)}>
                        <Icon name="edit" size={18} />
                      </IconButton>
                      <IconButton variant="plain" label={t("common.delete")} onClick={() => setDeleteFolderTarget(folder._id)}>
                        <Icon name="trash" size={18} />
                      </IconButton>
                    </div>
                  )}
                  <button
                    type="button"
                    disabled={isRenaming}
                    onClick={() => openFolder(folder._id)}
                    aria-label={folder.name}
                    className="flex h-[110px] w-full items-center justify-center rounded-radius-md bg-fill-quaternary text-status-warning"
                  >
                    <FiFolder className="size-8" />
                  </button>
                  {isRenaming ? (
                    <RenameField name={folder.name} value={renameValue} onChange={setRenameValue} onSave={submitRename} onCancel={cancelRename} />
                  ) : (
                    <p className="truncate text-subheadline-semibold text-label-primary">{folder.name}</p>
                  )}
                </div>
              );
            })}

            {files.map((file) => {
              const isRenaming = renaming?.type === "file" && renaming.id === file._id;
              const isPreviewable = isPreviewableCloudFile(file);
              return (
                <div
                  key={file._id}
                  draggable={canManage && !isRenaming}
                  onDragStart={(e) => onDragStart(e, "file", file._id)}
                  onDragEnd={onDragEnd}
                  onDragOver={(e) => e.stopPropagation()}
                  onDoubleClick={() => isPreviewable && openPreview(file)}
                  className={fileCard(false)}
                >
                  {!isRenaming && (
                    <div className={CARD_ACTIONS}>
                      {canManage && (
                        <IconButton variant="plain" label={t("common.edit")} onClick={() => startRename("file", file)}>
                          <Icon name="edit" size={18} />
                        </IconButton>
                      )}
                      <a
                        href={file.url}
                        download={file.name}
                        aria-label={t("cloud.download")}
                        className={`relative flex size-8 items-center justify-center rounded-full text-label-primary ${STATE_LAYER}`}
                      >
                        <span className="rotate-180">
                          <Icon name="upload" size={18} />
                        </span>
                      </a>
                      {canManage && (
                        <IconButton variant="plain" label={t("common.delete")} onClick={() => setDeleteFileTarget(file._id)}>
                          <Icon name="trash" size={18} />
                        </IconButton>
                      )}
                    </div>
                  )}
                  <button
                    type="button"
                    disabled={!isPreviewable || isRenaming}
                    onClick={() => isPreviewable && openPreview(file)}
                    aria-label={file.name}
                    className="flex h-[110px] w-full items-center justify-center rounded-radius-md bg-fill-quaternary"
                  >
                    <CloudFileIcon mintSpaceId={mintSpaceId} file={file} isDark={isDark} className="h-12 w-12" />
                  </button>
                  {isRenaming ? (
                    <RenameField name={file.name} value={renameValue} onChange={setRenameValue} onSave={submitRename} onCancel={cancelRename} />
                  ) : (
                    <p className="truncate text-subheadline-semibold text-label-primary">{file.name}</p>
                  )}
                  <p className="text-footnote text-label-tertiary">{formatBytes(file.size)}</p>
                  {isPreviewable && !isRenaming && (
                    <Button
                      size="s"
                      variant="tinted"
                      fullWidth
                      icon={<Icon name="eye" size={16} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        openPreview(file);
                      }}
                    >
                      {t("cloud.preview")}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
        </div>
      </div>

      {/* Preview modal */}
      <Modal
        open={!!preview}
        onClose={closePreview}
        title={preview?.file.name}
        description={preview ? formatBytes(preview.file.size) : undefined}
        closeLabel={t("common.close")}
        isDark={isDark}
        width="min-[600px]:w-[760px]"
      >
        {preview && (
          <>
            <div className="min-h-0 overflow-auto">
              {preview.kind === "text" && (
                previewLoading ? (
                  <p className={mutedText}>{t("cloud.loadingPreview")}</p>
                ) : previewEditing ? (
                  <textarea
                    value={previewText}
                    onChange={(e) => setPreviewText(e.target.value)}
                    spellCheck={false}
                    className="h-[min(60vh,440px)] w-full resize-none rounded-radius-md bg-fill-tertiary p-space-16 font-mono text-subheadline text-label-primary outline-none"
                  />
                ) : (
                  <pre
                    className="whitespace-pre-wrap break-words rounded-radius-md bg-fill-quaternary p-space-16 font-mono text-subheadline text-label-primary"
                  >
                    {previewText}
                  </pre>
                )
              )}
              {preview.kind === "image" && (
                previewLoading || !previewMediaUrl ? (
                  <p className={mutedText}>{t("cloud.loadingPreview")}</p>
                ) : (
                  <div className="flex items-center justify-center">
                    <img
                      src={previewMediaUrl}
                      alt={preview.file.name}
                      className="max-h-[70vh] w-auto max-w-full rounded-radius-xs object-contain shadow-lg"
                    />
                  </div>
                )
              )}
              {preview.kind === "mp3" && (
                previewLoading || !previewMediaUrl ? (
                  <p className={mutedText}>{t("cloud.loadingPreview")}</p>
                ) : (
                  <audio controls className="w-full" src={previewMediaUrl}>
                    <track kind="captions" />
                  </audio>
                )
              )}
              {preview.kind === "mp4" && (
                previewLoading || !previewMediaUrl ? (
                  <p className={mutedText}>{t("cloud.loadingPreview")}</p>
                ) : (
                  <div className="flex items-center justify-center">
                    {useNativeVideoControls ? (
                      <video
                        controls
                        className="mx-auto block h-auto max-h-[70vh] w-auto max-w-full rounded-radius-xs object-contain"
                        src={previewMediaUrl}
                      >
                        <track kind="captions" />
                      </video>
                    ) : (
                      <CustomVideoPlayer
                        src={previewMediaUrl}
                        isDark={isDark}
                        className="max-h-[70vh]"
                      />
                    )}
                  </div>
                )
              )}
            </div>
            {preview.kind === "text" && isEditableTextFile(preview.file) && canManage && !previewLoading && (
              <div className="flex justify-end gap-space-12">
                {previewEditing ? (
                  <>
                    <Button
                      variant="gray"
                      disabled={previewSaving}
                      onClick={() => {
                        setPreviewEditing(false);
                        openPreview(preview.file);
                      }}
                    >
                      {t("common.cancel")}
                    </Button>
                    <Button loading={previewSaving} onClick={savePreviewText}>
                      {previewSaving ? t("cloud.saving") : t("common.save")}
                    </Button>
                  </>
                ) : (
                  <Button variant="gray" icon={<Icon name="edit" size={20} />} onClick={() => setPreviewEditing(true)}>
                    {t("cloud.editFile")}
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </Modal>

      <PromptModal
        isOpen={showNewFolderModal}
        onClose={() => !modalLoading && setShowNewFolderModal(false)}
        onSubmit={handleCreateFolder}
        title={t("cloud.newFolder")}
        placeholder={t("cloud.folderNamePrompt")}
        submitLabel={t("cloud.createFolder")}
        isDark={isDark}
        loading={modalLoading}
      />

      <ConfirmModal
        isOpen={!!deleteFolderTarget}
        onClose={() => !modalLoading && setDeleteFolderTarget(null)}
        onConfirm={handleDeleteFolder}
        title={t("cloud.deleteFolderTitle")}
        message={t("cloud.confirmDeleteFolder")}
        confirmLabel={t("common.delete")}
        isDark={isDark}
        variant="danger"
        loading={modalLoading}
      />

      <ConfirmModal
        isOpen={!!deleteFileTarget}
        onClose={() => !modalLoading && setDeleteFileTarget(null)}
        onConfirm={handleDeleteFile}
        title={t("cloud.deleteFileTitle")}
        message={t("cloud.confirmDeleteFile")}
        confirmLabel={t("common.delete")}
        isDark={isDark}
        variant="danger"
        loading={modalLoading}
      />
    </MintSpaceAppWindow>
  );
};

export default MintSpaceCloudPage;
